import React from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Modal,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  Textarea,
  Button,
  Content,
} from 'ui'

interface ICreateReportModalProps {
  confirmLabel: string
  loading?: boolean
  onDismiss: () => void
  onConfirm: (name: string, description: string) => void
  placeholder?: string
  title: string
}

// Validation schema
const schema = yup.object({
  name: yup.string().required('Report name is required'),
  description: yup.string().optional(),
})

type FormData = yup.InferType<typeof schema>

/**
 * Renders a modal that enables the creation of a custom report.
 * @param props Configuration for the modal's initial values and event handlers.
 */
export const CreateReportModal: React.FC<ICreateReportModalProps> = ({
  confirmLabel,
  loading,
  onConfirm: handleConfirm,
  onDismiss: handleDismiss,
  placeholder,
  title,
}) => {
  const form = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      description: '',
    },
  })

  const onSubmit = (data: FormData) => {
    handleConfirm(data.name, data.description || '')
  }

  return (
    <Modal.View onDismiss={handleDismiss} loading={loading}>
      <div className="flex-col flex p-6 w-full">
        <Content.HeadingSmall className="mb-4">{title}</Content.HeadingSmall>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Report Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={placeholder || 'Enter report name'}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter a brief description of what this report will be for."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-3 justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleDismiss}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || !form.formState.isValid}
              >
                {confirmLabel}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </Modal.View>
  )
}
